import React from "react";
import Utils from "Utils/utils";
import "./jobChange.scss";

// A job change Clean up found (contact.jobChange: {status, fromCompany, toCompany, otherContactId}).
// LEFT is on the contact they left behind; JOINED on the one created at their new company.
export const isMoved = jobChange => jobChange?.status === "LEFT" && !Utils.isSalesIntelHost();
export const isNewJob = jobChange => jobChange?.status === "JOINED" && !Utils.isSalesIntelHost();

const LeftIcon = () => (
  <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2.5H3.5v11H6M9.5 5l3 3-3 3M12.5 8H6" />
  </svg>
);

const NewIcon = () => (
  <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 11l6-6M6 5h5v5" />
  </svg>
);

const hoverText = jobChange =>
  isMoved(jobChange)
    ? `Left ${jobChange.fromCompany || "this company"}${jobChange.toCompany ? ` · now at ${jobChange.toCompany}` : ""}`
    : `New job${jobChange.fromCompany ? ` · previously at ${jobChange.fromCompany}` : ""}`;

/**
 * "Moved" / "New job" marker next to a contact's name. {compact} shows just the icon in a small
 * dot (for tight table columns); the full wording is on hover either way. Nothing when there's
 * no job change.
 */
export const JobChangeTag = ({ jobChange, compact = false }) => {
  if (!isMoved(jobChange) && !isNewJob(jobChange)) return null;
  const moved = isMoved(jobChange);
  const label = moved ? "Moved" : "New job";
  return (
    <span
      className={`jobChangeTag ${moved ? "moved" : "newJob"} ${compact ? "compact" : ""}`}
      title={hoverText(jobChange)}
      aria-label={compact ? `${label}: ${hoverText(jobChange)}` : undefined}
    >
      {moved ? <LeftIcon /> : <NewIcon />}
      {!compact && label}
    </span>
  );
};

/**
 * Banner at the top of the contact sidebar: where they went (or came from), with a link to the
 * other contact. {onOpenContact(contactId)} opens it.
 */
export const JobChangeBanner = ({ jobChange, onOpenContact }) => {
  if (!isMoved(jobChange) && !isNewJob(jobChange)) return null;
  const moved = isMoved(jobChange);
  const other = jobChange.otherContactId;
  return (
    <div className={`jobChangeBanner ${moved ? "moved" : "newJob"}`}>
      <span className="jobChangeBannerIcon">{moved ? <LeftIcon /> : <NewIcon />}</span>
      <div>
        {moved ? (
          <>
            Left {jobChange.fromCompany || "this company"}.
            {jobChange.toCompany && ` Now at ${jobChange.toCompany}.`}
          </>
        ) : (
          <>New job{jobChange.fromCompany && ` — previously at ${jobChange.fromCompany}`}.</>
        )}
        {other && onOpenContact && (
          <button type="button" className="jobChangeBannerLink" onClick={() => onOpenContact(other)}>
            {moved ? "View new contact" : "View previous contact"}
          </button>
        )}
      </div>
    </div>
  );
};
